/**
 * @constructor
 * Item constructor.
 *
 * @param [values]
 * Value of the item.
 *
 * @param [subValuesProperty]
 * Name of a property that holds a list of values that should be used for sub items.
 *
 * @param [parent]
 * The parent item.
 *
 * @return {Object}
 *
 *      id:
 *      Unique id of the item.
 *
 *      selected:
 *      True when the item is selected, otherwise false.
 *
 *      disabled:
 *      True when the item is disabled, otherwise false.
 *
 *      value:
 *      The original value, where this item is created from.
 *
 *      subItems:
 *      List of sub items.
 *
 *      source:
 *      The items object this item belongs to.
 *
 *      parent:
 *      The parent items.
 *
 *      callback:
 *      Callback function that is called when the item is selected.
 */
export class Item {

    constructor(value, subValuesProperty, parent, callback) {
        this.source = null;
        this.id = uuid();
        this.disabled = false;
        this.value = value;
        this.subItems = [];
        this._parent = parent;
        this._selected = false;
        this.callback = callback;

        if (subValuesProperty != null) {
            var _self = this;

            value[subValuesProperty].forEach(function (subValue) {
                _self.subItems.push(new Item(subValue, null, _self, callback));
            });
        }
    }

    /**
     * Creates a copy of the item and all sub items.
     *
     * @returns {Item} Copy of the item.
     */
    copy() {
        var copy = new Item();
        copy.source = this.source;
        copy.id = this.id;
        copy.disabled = this.disabled;
        copy.value = this.value;
        copy._parent = this.parent;
        copy._selected = this.selected;
        copy.callback = this.callback;

        copy.subItems = this.subItems.map((subItem)=> {
            return subItem.copy();
        });

        return copy;
    }

    /**
     *  Add an item as sub item.
     *
     * @param item
     *  The item to add.
     *
     * @param asCopy
     *  When true a copy of the item is added instead of the original one.
     *
     * @returns {*}
     *  The added item.
     */
    add(item, asCopy) {
        if (asCopy) {
            item = item.copy();
        }

        this.subItems.push(item);
        item._parent = this;
        item.source = this.source;
        if (!item.callback) {
            item.callback = this.callback;
        }

        return item;
    }

    /**
     *  All all items as sub items.
     *
     * @param items
     *  The items to add.
     *
     * @param asCopy
     *  When true a copy of the items is added instead of the original one.
     */
    addAll(items, asCopy) {
        items.forEach((item)=> {
            this.add(item, asCopy);
        });
    }

    get parent() {
        return this._parent;
    }

    set parent(parent) {
        var _self = this;
        _self.parent = parent;

        _self.subItems.forEach(function (subItem) {
            subItem.parent = parent;
        });
    }

    get selected() {
        return this._selected;
    }

    /**
     * Selects or deselect the item.
     *
     * When the item has sub items all sub items are selected or deselected.
     * When the item has no sub items the parent item is selected when any
     * sub item is selected or deselected otherwise.
     *
     * @param selected
     * When true the item is selected, otherwise deselected.
     */

    set selected(selected) {
        var _self = this;

        if (_self.disabled || _self._selected == selected) {
            return;
        }

        // Set all items to deselected.
        if (_self.source && _self.source.singleSelection == true) {
            _self.source.all.forEach(function (item) {
                item._selected = false;

                item.subItems.forEach(function (subItem) {
                    subItem._selected = false;
                });
            });
        }

        _self._selected = selected;

        if (_self.subItems.length > 0) {
            _self.subItems.forEach(function (subItem) {
                subItem._selected = _self._selected;
            });

        } else {
            if (_self.parent) {
                var selectedSubItem = _self.parent.subItems.find(function (subItem) {
                    return subItem._selected;
                });

                _self.parent._selected = selectedSubItem != null;
            }
        }

        if (_self.source) {
            _self.source.selected = findSelectedItems(_self.source.all, true, true);
        }

        if (_self.callback != null) {
            _self.callback(_self);
        }
    }

    /**
     * Selects or deselect the item.
     *
     * @param selected
     * When true the item is selected, otherwise deselected.
     */
    select(selected) {
        this.selected = selected;
    };

    /**
     * Toggle the item selected.
     */
    toggle() {
        this.selected = !this.selected;
    };

    /**
     * Remove the item from its source list.
     */
    remove() {
        this.source.remove(this);
    };

    /**
     * Returns true when the item has sub items.
     */
    hasSubItems(){
        return this.subItems.length > 0;
    }

    /**
     * Returns the first sub item.
     */
    firstSubItem(){
        return this.hasSubItems() ? this.subItems[0] : null;
    }

    /**
     * Returns the last sub item.
     */
    lastSubItem(){
        return this.hasSubItems() ? this.subItems[this.subItems.length -1] : null;
    }

}

/**
 * Items constructor.
 *
 * @param values
 * @param subValuesProperty
 * @constructor
 */
export class Items {

    constructor() {
        this.singleSelection = false;
        this.all = [];
        this.selected = [];
    }

    /**
     * Create a new items object and add all values as item.
     *
     * @param value
     *      List of values.
     *
     *  @Param subValuesProperty
     *      Name of a property that holds a list of values that should be used for sub items.
     *
     * @return
     *      List of items (objects).
     */

    static create(values, subValuesProperty, callback) {
        var items = new Items();

        values.forEach(function (value) {
            var item = new Item(value, subValuesProperty, null, callback);
            items.add(item);
        });

        return items;
    }

    /**
     * Returns all selected items.
     *
     * @param withParentItems
     *  When true selected parent items are included.
     *
     * @param withSubItems
     *  When true selected sub items are included.
     *
     * @returns {Array}
     *  List of all selected items.
     */
    selected(withParentItems, withSubItems) {
        if (withParentItems == false || withSubItems == false) {
            return findSelectedItems(this.all, withParentItems, withSubItems);
        } else {
            return this.selected;
        }
    }

    /**
     * Returns true when any item is selected.
     */
    hasSelected() {
        return this.selected.length > 0;
    }

    /**
     * Get the values of all selected items.
     *
     * @param withParentItems
     * When true the values of selected parent items are included.
     *
     * @param withSubItems
     * When true the values of selected sub items are included.
     *
     * @returns {Array}
     * The values of all selected items.
     */
    selectedValues(withParentItems, withSubItems) {
        var items = findSelectedItems(this.all, withParentItems, withSubItems);
        var values = items.map(function (item) {
            return item.value;
        });

        return values;
    }

    /**
     * Returns the value of the first selected item, or null if no item is selected.
     *
     * @param withParentItems
     *  When true the value of an selected parent items is included.
     *
     * @param withSubItems
     *  When true the value of an selected sub items is included.
     *
     * @returns {null}
     */
    selectedValue(withParentItems, withSubItems) {
        var selectedValues = this.selectedValues(withParentItems, withSubItems);
        return selectedValues.length > 0 ? selectedValues[0] : null;
    }

    selectBy(value, property) {
        var item = this.findFirstBy(value, property);
        if (item) {
            item.select(true);
        }
    }

    selectAllBy(values, property) {
        var self = this;

        values.forEach((value)=> {
            self.selectBy(value, property);
        });
    }

    /**
     * Find a item by a property and/or value combination, or by item id.
     *
     * @param value
     * The value of the property or an object containing the property value.
     *
     * @param property
     * The property field.
     *
     * @returns {Array}
     * Array of all items.
     */
    findBy(value, property) {
        var items = [];

        if (typeof value == 'object') {
            value = value[property];
        }

        this.all.forEach(function (item) {
            if (property == null) {
                if (item.id == value || item.value == value) {
                    items.push(item);
                }
            } else {
                if (item.value[property] == value) {
                    items.push(item);
                }
            }

            item.subItems.forEach(function (subItem) {
                if (property == null) {
                    if (subItem.id == value || subItem.value == value) {
                        items.push(subItem);
                    }
                } else {
                    if (subItem.value[property] == value) {
                        items.push(subItem);
                    }
                }
            });
        });

        return items;
    }

    findFirstBy(value, property) {
        var items = this.findBy(value, property);
        return items.length > 0 ? items[0] : null;
    }

    /**
     * Find the index of an item.
     *
     * @param item
     * The item
     *
     * @returns {number}
     * Index of the item or -1 if not present.
     */
    indexOf(item) {
        return indexOfItem(item, this.all);
    }

    /**
     * Check if an item is present.
     *
     * @returns {boolean}
     * True if the item is present, false otherwise.
     */
    contains(item) {
        return indexOfItem(item, this.all) > 0;
    }

    /**
     * Add an item if not already present.
     *
     * @param item
     * The item to add.
     */
    add(item) {
        var _self = this;

        if (indexOfItem(item, _self.all) == -1) {
            _self.all.push(item);
            item.source = _self;

            item.subItems.forEach(function (subItem) {
                subItem.source = _self;
            });
        }

        return item;
    }

    /**
     * Remove an item if present.
     *
     * @param item
     * The item to remove
     *
     * @returns
     * True if the item was removed, false otherwise.
     */
    remove(item) {
        var _self = this;

        if (item.source != _self) {
            throw 'Item belongs not to list.';
        }

        removeItem(item, _self.all);
        removeItem(item, _self.selected);
        item.source = null;

        item.subItems.forEach(function (subItem) {
            _self.remove(subItem);
        });
    }

    /**
     * Resets all selected items.
     */
    reset() {
        this.all.forEach(function (item) {
            item.selected = false;
        });

        this.selected = [];
    }

    /**
     * Clear all items
     */
    clear() {
        this.all = [];
        this.selected = [];
    }

    /**
     * Returns true when no items are defined.
     */
    isEmpty() {
        return this.all.length == 0;
    }

    /**
     * Returns the first item.
     */
    first() {
        return this.isEmpty() ? null : this.all[0];
    }

    /**
     * Returns the last item.
     */
    last() {
        return this.isEmpty() ? null : this.all[this.all.length - 1];
    }

    merge(items) {
        items.all.forEach((item)=> {
            var selfItem = this.findFirstBy(item.id) || this.findFirstBy(item.value);
            if (selfItem) {
                selfItem.addAll(item.subItems);
            } else {
                this.add(item);
            }
        });
    }

}

/****************************************
 *   Private Functions
 ****************************************/

/**
 * Creates a new UUID.
 *
 * @returns {string}
 */
function uuid() {
    var d = new Date().getTime();
    var uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        var r = (d + Math.random() * 16) % 16 | 0;
        d = Math.floor(d / 16);
        return (c == 'x' ? r : (r & 0x3 | 0x8)).toString(16);
    });

    return uuid;
}

function findSelectedItems(sourceList, withParentItems, withSubItems, targetList) {
    if (withParentItems == null) {
        withParentItems = true;
    }

    if (withSubItems == null) {
        withSubItems = true;
    }

    var items = targetList != null ? targetList : [];

    sourceList.forEach(function (item) {
        var isParent = item.parent == null || (item.subItems && item.subItems.length > 0);

        if (isParent && item.selected) {
            if (withParentItems) {
                items.push(item);
            }

            if (withSubItems) {
                findSelectedItems(item.subItems, withParentItems, withSubItems, items);
            }

        } else {
            if (withSubItems && item.selected) {
                items.push(item);
            }
        }
    });

    return items;
}

function indexOfItem(item, list) {
    var idx = list.findIndex(function (_item) {
        return _item.id == item.id;
    });

    return idx;
}

function removeItem(item, list) {
    var idx = indexOfItem(item, list);

    if (idx >= 0) {
        list.splice(idx, 1);
        return true;
    } else {
        return false;
    }
}



// WEBPACK FOOTER //
// ./src/modules/core/model/items.model.js